import { Link } from '@tanstack/react-router';
import type { ColumnDef, SortingState } from '@tanstack/react-table';

import type { EquityRow } from '#/api/generated/fINRAMarketDataAPI.schemas';
import type { EquitySortColumnId } from '#/components/index/EquitiesExploreTable/types';
import { Indicator } from '#/components/ui/Indicator';
import { NoData } from '#/components/ui/NoData';
import { Ticker } from '#/components/ui/Ticker';
import { DASH } from '#/utils/constants';
import {
  formatCompactCurrency,
  formatCurrency,
  roundDecimal,
} from '#/utils/format';

export function equityRowId(row: EquityRow, index: number) {
  return `equity-${row.ticker ?? row.name}-${index}`;
}

export function getSortColumn(
  sorting: SortingState,
  fallbackSortColumn: EquitySortColumnId,
): EquitySortColumnId {
  const columnId = sorting[0]?.id;

  if (
    columnId === 'price' ||
    columnId === 'trailing_1yr_return' ||
    columnId === 'trailing_5yr_return' ||
    columnId === 'market_cap'
  ) {
    return columnId;
  }

  return fallbackSortColumn;
}

export function getSortDirection(sorting: SortingState): 'asc' | 'desc' {
  return sorting[0]?.desc === false ? 'asc' : 'desc';
}

const API_SORT_KEY_BY_COLUMN: Record<
  EquitySortColumnId,
  'price' | 'return_1y' | 'return_5y' | 'market_cap'
> = {
  price: 'price',
  trailing_1yr_return: 'return_1y',
  trailing_5yr_return: 'return_5y',
  market_cap: 'market_cap',
};

export function getApiSortKey(sortColumn: EquitySortColumnId) {
  return API_SORT_KEY_BY_COLUMN[sortColumn];
}

export type EquityColumnId =
  | 'ticker'
  | 'price'
  | 'trailing_1yr_return'
  | 'trailing_5yr_return'
  | 'market_cap';

interface EquityColumnOptions {
  compact?: boolean;
  /**
   * Overrides the ticker link's analytics annotation. Defaults to the explore
   * table's `explore_table_ticker` / `equities_explore`; the search page passes
   * `search_result_row` / `search_results` so its rows are attributed
   * distinctly without affecting other tables.
   */
  tickerTrack?: { track: string; context: string };
}

const COMPACT_TICKER_COLUMN = {
  size: 170,
  minSize: 130,
  maxSize: 170,
} as const;

const COMPACT_PRICE_COLUMN = {
  size: 80,
  minSize: 72,
  maxSize: 80,
} as const;

const COMPACT_RETURN_COLUMN = {
  size: 108,
  minSize: 96,
  maxSize: 108,
} as const;

export function equitiesColumns(
  firstColumnLabel: string,
  visibleColumns: EquityColumnId[] = [
    'ticker',
    'price',
    'trailing_5yr_return',
    'market_cap',
  ],
  options?: EquityColumnOptions,
): ColumnDef<EquityRow>[] {
  const compact = options?.compact ?? false;
  const tickerTrack = options?.tickerTrack ?? {
    track: 'explore_table_ticker',
    context: 'equities_explore',
  };
  const tickerSizing = compact
    ? COMPACT_TICKER_COLUMN
    : { size: 300, minSize: 220, maxSize: undefined };

  const columns: Record<EquityColumnId, ColumnDef<EquityRow>> = {
    ticker: {
      id: 'ticker',
      accessorKey: 'ticker',
      header: compact ? 'Ticker' : firstColumnLabel,
      enableSorting: false,
      size: tickerSizing.size,
      minSize: tickerSizing.minSize,
      maxSize: tickerSizing.maxSize,
      meta: { announceHeader: true },
      cell: ({ row }) => {
        if (!row.original.ticker && !row.original.name) {
          return <NoData />;
        }

        const title = row.original.name ?? DASH;

        if (!row.original.ticker) {
          return (
            <span className="flex min-w-0 items-center gap-8">
              <Ticker
                symbol={row.original.ticker ?? DASH}
                size={compact ? 'sm' : 'md'}
              />
              <span className="text-static-12 min-w-0 truncate">{title}</span>
            </span>
          );
        }

        return (
          <span className="flex min-w-0 items-center gap-8">
            <Ticker
              symbol={row.original.ticker ?? DASH}
              size={compact ? 'sm' : 'md'}
            />
            <Link
              className="text-static-12 min-w-0 truncate"
              to="/$category/$id"
              params={{ category: 'equities', id: row.original.ticker }}
              data-track={tickerTrack.track}
              data-track-label={row.original.ticker}
              data-track-context={tickerTrack.context}
            >
              {title}
            </Link>
          </span>
        );
      },
    },
    price: {
      id: 'price',
      header: 'Price',
      accessorKey: 'price',
      enableSorting: true,
      ...(compact ? COMPACT_PRICE_COLUMN : {}),
      meta: { align: 'end', announceHeader: true },
      cell: ({ getValue }) => {
        return formatCurrency(getValue<number | null>()) ?? <NoData />;
      },
    },
    trailing_1yr_return: {
      id: 'trailing_1yr_return',
      accessorKey: 'trailing_1yr_return',
      header: '1-Year Return',
      enableSorting: true,
      ...(compact ? COMPACT_RETURN_COLUMN : {}),
      meta: { align: 'end', announceHeader: true },
      cell: ({ getValue }) => {
        const value = getValue<number | null>() ?? null;

        if (value === null) {
          return <NoData />;
        }

        return (
          <Indicator
            className={compact ? 'text-static-12' : 'text-static-14'}
            size={compact ? 'sm' : 'md'}
            value={roundDecimal(value) ?? 0}
            suffix="%"
            type="percent"
          />
        );
      },
    },
    trailing_5yr_return: {
      id: 'trailing_5yr_return',
      accessorKey: 'trailing_5yr_return',
      header: '5-Year Return',
      enableSorting: true,
      ...(compact ? COMPACT_RETURN_COLUMN : {}),
      meta: { align: 'end', announceHeader: true },
      cell: ({ getValue }) => {
        const value = getValue<number | null>() ?? null;

        if (value === null) {
          return <NoData />;
        }

        return (
          <Indicator
            className={compact ? 'text-static-12' : 'text-static-14'}
            size={compact ? 'sm' : 'md'}
            value={roundDecimal(value) ?? 0}
            suffix="%"
            type="percent"
          />
        );
      },
    },
    market_cap: {
      accessorKey: 'market_cap',
      header: 'Market Cap',
      enableSorting: true,
      ...(compact ? COMPACT_RETURN_COLUMN : {}),
      meta: { align: 'end', announceHeader: true },
      cell: ({ getValue }) =>
        formatCompactCurrency(getValue<number | null>()) ?? <NoData />,
    },
  };

  return visibleColumns.map((columnId) => columns[columnId]);
}
